import { LoadingOutlined, ShopOutlined, UserOutlined } from '@ant-design/icons';
import { Button, Form, Input, message, Modal, Select, Steps, Typography } from 'antd';
import { useState } from 'react';
import axios from '../config/axios';
import { handleAPIError } from '../utils/helpers';
import { useQueryClient } from '@tanstack/react-query';
import CopyableText from './copyable-text';
import { BsPhone } from 'react-icons/bs';

const { Step } = Steps;
const { Title, Text } = Typography;

interface UserFormValues {
	phone_number: string;
	first_name: string;
	last_name: string;
	email: string;
	password: string;
}

interface RetailerFormValues {
	business_name: string;
	district: string;
	town: string;
	shop_number: string;
	user: number;
}

interface CreateRetailerProps {
	open: boolean;
	onClose: () => void;
	onSuccess?: () => void;
}

const CreateRetailer = ({ open, onClose, onSuccess }: CreateRetailerProps) => {
	const [currentStep, setCurrentStep] = useState<number>(0);
	const [userForm] = Form.useForm<UserFormValues>();
	const [retailerForm] = Form.useForm<RetailerFormValues>();
	const [userId, setUserId] = useState<number | null>(null);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isComplete, setIsComplete] = useState<boolean>(false);
	const [registeredUser, setRegisteredUser] = useState<{ phone_number: string; password: string } | null>(null);
	const [countryCode, setCountryCode] = useState<string>('256');

	const queryClient = useQueryClient();

	const handleUserRegistration = async (values: UserFormValues) => {
		setIsLoading(true);
		try {
			// Add country code to phone number
			const phoneWithCode = `${countryCode}${values.phone_number}`;

			const response = await axios.post(
				'/users/register/admin',
				{ ...values, phone_number: phoneWithCode, role: 'retailer' },
				{
					withCredentials: true,
				},
			);

			const newUserId = response.data.results.user.id;
			setUserId(newUserId);

			// Ensure the retailer form has the user ID
			retailerForm.setFieldValue('user', newUserId);

			// Store the user credentials for the success screen
			setRegisteredUser({
				phone_number: phoneWithCode,
				password: values.password,
			});

			message.success('User account created successfully!');
			setCurrentStep(1);
		} catch (error) {
			handleAPIError(error as Error);
		} finally {
			setIsLoading(false);
		}
	};

	const handleRetailerRegistration = async (values: RetailerFormValues) => {
		setIsLoading(true);
		values.user = userId as number;
		try {
			await axios.post('/admin/retailers/register', values, {
				withCredentials: true,
			});

			message.success('Retailer registered successfully!');
			setIsComplete(true);

			// Refetch the retailers query
			queryClient.invalidateQueries({ queryKey: ['retailers'] });

			// Call onSuccess callback if provided
			if (onSuccess) {
				onSuccess();
			}

			// Reset forms
			userForm.resetFields();
			retailerForm.resetFields();
		} catch (error) {
			handleAPIError(error as Error);
		} finally {
			setIsLoading(false);
		}
	};

	const handleModalClose = () => {
		// Reset the form state
		setCurrentStep(0);
		setUserId(null);
		setIsComplete(false);
		userForm.resetFields();
		retailerForm.resetFields();

		// Call the parent's onClose function
		onClose();
	};

	const handleStartOver = () => {
		setCurrentStep(0);
		setUserId(null);
		setIsComplete(false);
		userForm.resetFields();
		retailerForm.resetFields();
	};

	const selectBefore = (
		<Select
			defaultValue={'+256'}
			prefix={<BsPhone />}
			onChange={(value) => setCountryCode(value)}
		>
			<Select.Option value="256">+256</Select.Option>
			<Select.Option value="88">+88</Select.Option>
		</Select>
	);

	const steps = [
		{
			title: 'Account Details',
			icon: <UserOutlined />,
			content: (
				<Form
					form={userForm}
					layout="vertical"
					onFinish={handleUserRegistration}
					requiredMark={false}
					disabled={isLoading}
					initialValues={{}}
				>
					<Title level={4}>Register Retailer Account</Title>
					<Text
						type="secondary"
						className="mb-6 block"
					>
						Create a user account for the retailer
					</Text>

					<Form.Item
						name="phone_number"
						label="Phone Number"
						rules={[
							{ required: true, message: 'Please enter the phone number' },
							{ pattern: /^\d+$/, message: 'Please enter a valid phone number' },
						]}
					>
						<Input
							addonBefore={selectBefore}
							placeholder="Phone number"
							onKeyDown={(e) => {
								// If the key is not a number (0-9), prevent input
								if (!/^\d$/.test(e.key) && !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)) {
									e.preventDefault();
								}
							}}
							onChange={(e) => {
								// Remove any non-numeric characters
								e.target.value = e.target.value.replace(/\D/g, '');
							}}
						/>
					</Form.Item>

					<Form.Item
						name="first_name"
						label="First Name"
						rules={[{ required: true, message: 'Please enter the first name' }]}
					>
						<Input placeholder="First name" />
					</Form.Item>

					<Form.Item
						name="last_name"
						label="Last Name"
						rules={[{ required: true, message: 'Please enter the last name' }]}
					>
						<Input placeholder="Last name" />
					</Form.Item>

					<Form.Item
						name="email"
						label="Email"
						rules={[
							{ required: true, message: 'Please enter the email' },
							{ type: 'email', message: 'Please enter a valid email' },
						]}
					>
						<Input placeholder="Email address" />
					</Form.Item>

					<Form.Item
						name="password"
						label="Password"
						rules={[{ required: true, message: 'Please enter a password' }]}
					>
						<Input.Password placeholder="Password" />
					</Form.Item>

					<Form.Item className="mb-0">
						<Button
							type="primary"
							htmlType="submit"
							loading={isLoading}
							className="w-full"
						>
							Create Account & Continue
						</Button>
					</Form.Item>
				</Form>
			),
		},
		{
			title: 'Business Details',
			icon: <ShopOutlined />,
			content: (
				<Form
					form={retailerForm}
					layout="vertical"
					onFinish={handleRetailerRegistration}
					requiredMark={false}
					disabled={isLoading || !userId}
					initialValues={{ user: userId }}
					preserve={false}
				>
					<Title level={4}>Register Retailer Profile</Title>
					<Text
						type="secondary"
						className="mb-6 block"
					>
						Complete the retailer's business information
					</Text>

					<Form.Item
						name="business_name"
						label="Business Name"
						rules={[{ required: true, message: 'Please enter the business name' }]}
					>
						<Input
							placeholder="Business name"
							prefix={<ShopOutlined />}
						/>
					</Form.Item>

					<Form.Item
						name="district"
						label="District"
						rules={[{ required: true, message: 'Please enter the district' }]}
					>
						<Input placeholder="District" />
					</Form.Item>

					<Form.Item
						name="town"
						label="Town"
						rules={[{ required: true, message: 'Please enter the town' }]}
					>
						<Input placeholder="Town" />
					</Form.Item>

					<Form.Item
						name="shop_number"
						label="Shop Address"
						rules={[{ required: true, message: 'Please enter the shop address' }]}
					>
						<Input placeholder="Shop number and address" />
					</Form.Item>

					<Form.Item
						name="user"
						hidden
					>
						<Input type="hidden" />
					</Form.Item>

					<Form.Item className="mb-0">
						<div className="flex gap-3">
							<Button
								onClick={() => setCurrentStep(0)}
								disabled={isLoading}
								className="flex-1"
							>
								Back
							</Button>
							<Button
								type="primary"
								htmlType="submit"
								loading={isLoading}
								className="flex-1"
							>
								Register Retailer
							</Button>
						</div>
					</Form.Item>
				</Form>
			),
		},
	];

	const modalContent = isComplete ? (
		<div className="py-8 text-center">
			<div className="mb-6 text-center">
				<div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
					<span className="text-2xl text-green-500">✓</span>
				</div>
				<Title level={3}>Registration Complete!</Title>
				<Text className="text-gray-500">The retailer has been successfully registered</Text>

				{registeredUser && (
					<div className="mt-6 rounded-md border border-gray-200 bg-gray-50 p-4 text-left">
						<Title
							level={5}
							className="mb-3"
						>
							Retailer Login Credentials
						</Title>
						<div className="space-y-3">
							<div>
								<Text strong>Phone Number:</Text>
								<div className="mt-1">
									<CopyableText text={registeredUser.phone_number} />
								</div>
							</div>
							<div>
								<Text strong>Password:</Text>
								<div className="mt-1">
									<CopyableText text={registeredUser.password} />
								</div>
							</div>
						</div>
					</div>
				)}
			</div>
			<Button
				type="primary"
				onClick={handleStartOver}
			>
				Register Another Retailer
			</Button>
		</div>
	) : (
		<>
			<Steps
				current={currentStep}
				className="mb-8"
			>
				{steps.map((step) => (
					<Step
						key={step.title}
						title={step.title}
						icon={currentStep === steps.indexOf(step) && isLoading ? <LoadingOutlined /> : step.icon}
					/>
				))}
			</Steps>

			<div className="steps-content">{steps[currentStep].content}</div>
		</>
	);

	return (
		<Modal
			open={open}
			onCancel={handleModalClose}
			width={800}
			footer={null}
			destroyOnClose
			title="Register New Retailer"
			maskClosable={false}
			centered
		>
			{modalContent}
		</Modal>
	);
};

export default CreateRetailer;
